import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import {
  type UserRoleCreateSchema,
  type UserRoleQueryAllSchema,
  type UserRoleQuerySchema,
  type UserRoleUpdateSchema,
} from './schemas';

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, UserRoleCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/roles/',
        payload,
      }),
  });

export function useGetUserRoles(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['ROLES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<UserRoleQueryAllSchema>({
        endpoint: '/api/v1/roles',
        params: `${apiParamStr}&include=permissions`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['ROLES'] });
};

export const useGetUserRolesMutation: UseControlledMutation<UserRoleQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['USER_ROLES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<UserRoleQueryAllSchema>({
            endpoint: '/api/v1/roles',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetUserRole(id: string) {
  return useSuspenseQuery({
    queryKey: ['ROLE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: UserRoleQuerySchema }>({
        endpoint: `/api/v1/roles/${id}`,
        params: { include: 'permissions' },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: UserRoleQuerySchema }, ApiError, Partial<UserRoleUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/roles/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['ROLE_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/roles/${id}`,
        method: 'DELETE',
      }),
  });
